import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X } from "lucide-react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { Link } from "react-router";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  calculateDurationBetweenDates,
  formatISOToReadableDate,
  formatMinutesToDuration,
} from "@/utils/timeFormatting";
import Tooltip from "@/components/shared/Tooltip";
import { isPlanningKey, useMarkWaferRequestAsToSchedule } from "@/hooks/useRequestPlanning";
import { Badge } from "@/components/ui/badge";
import { formatWaferStatus, getWaferStatusColor } from "@/components/shared/utils";
import { PlanningRequestWafer } from "@/types/planning";
import { TestLabel } from "@/types/testLabel";
import { useIsAdmin } from "@/permissions";
import { DragHandle } from "./DragHandle";

type WaferRequestRowProps = {
  onWaferSelectionChange: (selection: Set<string>) => void;
  requestId: number;
  selectedWafers: Set<string>;
  showCheckboxes: boolean;
  status: "MEASURED" | "SCHEDULED";
  testLabel: Pick<TestLabel, "id" | "name">;
  wafer: PlanningRequestWafer;
  waferKey: string;
};
export function WaferRequestRow({
  onWaferSelectionChange,
  selectedWafers,
  showCheckboxes,
  requestId,
  status,
  testLabel,
  wafer,
  waferKey,
}: WaferRequestRowProps) {
  const { mutate } = useSWRConfig();
  const allowDragging = useIsAdmin();

  const { trigger: markAsToSchedule, isMutating } = useMarkWaferRequestAsToSchedule();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    disabled: !allowDragging || status === "MEASURED",
    id: `WaferRequest-${requestId}-${wafer._id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleWaferSelection = (waferId: string, checked: boolean) => {
    const newSelected = new Set(selectedWafers);

    if (checked) {
      newSelected.add(waferId);
    } else {
      newSelected.delete(waferId);
    }

    onWaferSelectionChange(newSelected);
  };

  const handleMarkingAsToSchedule = async () => {
    try {
      await markAsToSchedule(
        {
          measurement_request_id: requestId,
          wafer_id: wafer._id,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success("Successfully marked the wafer as To Schedule!");
            mutate(isPlanningKey);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <TableRow
      key={wafer._id}
      className={clsx("h-8", {
        "bg-yellow-500/30": isDragging,
        "cursor-grabbing": isDragging,
      })}
      ref={setNodeRef}
      style={style}
    >
      {status !== "MEASURED" && (
        <>
          <TableCell className="h-8" />
          {allowDragging && (
            <TableCell>
              {isDragging ? (
                <DragHandle isDragging />
              ) : (
                <DragHandle {...attributes} {...listeners} />
              )}
            </TableCell>
          )}
        </>
      )}
      {showCheckboxes ? (
        <TableCell className="h-8 py-1 w-px pl-0 pr-2">
          <Checkbox
            disabled={!allowDragging}
            checked={selectedWafers.has(waferKey)}
            onCheckedChange={(checked) => handleWaferSelection(waferKey, checked as boolean)}
          />
        </TableCell>
      ) : null}
      <TableCell className="font-medium h-8 py-1 text-sm">{wafer.name}</TableCell>
      <TableCell className="h-8 py-1">
        <Badge variant="outline" className="text-xs">
          {wafer.measurementSystem?.name ?? "Unassigned"}
        </Badge>
      </TableCell>
      {status === "SCHEDULED" && (
        <TableCell className="h-8 py-1 text-sm">{wafer.priority ?? "---"}</TableCell>
      )}
      <TableCell className="h-8 py-1">
        <Badge className={getWaferStatusColor(wafer.status)} variant="secondary">
          {formatWaferStatus(wafer.status)}
        </Badge>
      </TableCell>
      <TableCell className="h-8 py-1 text-sm">
        <Link to={`/test-labels/${testLabel.id}`} className="text-blue-600 hover:text-blue-800">
          {testLabel.name}
        </Link>
      </TableCell>
      <TableCell className="h-8 py-1 text-sm">{wafer.dieCount}</TableCell>
      <TableCell className="h-8 py-1 text-sm">
        {wafer.estimatedTime > 0 ? `${wafer.estimatedTime}min` : "---"}
      </TableCell>
      <TableCell className="h-8 py-1 text-sm">
        {wafer.estimatedTime > 0 && wafer.dieCount > 0
          ? formatMinutesToDuration(wafer.dieCount * wafer.estimatedTime)
          : "---"}
      </TableCell>
      {status === "MEASURED" && (
        <TableCell className="h-8 py-1 text-sm">
          {wafer.startedAt ? formatISOToReadableDate(wafer.startedAt) : "---"}
        </TableCell>
      )}
      {status === "MEASURED" && (
        <TableCell className="h-8 py-1 text-sm">
          {wafer.completedAt ? formatISOToReadableDate(wafer.completedAt) : "---"}
        </TableCell>
      )}
      {status === "MEASURED" && (
        <TableCell className="h-8 py-1 text-sm">
          {wafer.startedAt && wafer.completedAt
            ? calculateDurationBetweenDates(wafer.startedAt, wafer.completedAt)
            : "---"}
        </TableCell>
      )}
      {status === "SCHEDULED" && (
        <TableCell className="h-8 py-1 text-sm">
          {formatISOToReadableDate(wafer.estFinishTime)}
        </TableCell>
      )}
      <TableCell className="h-8 p-1 text-sm flex">
        {wafer.status === "SCHEDULED" ? (
          <Tooltip
            asChild
            trigger={
              <Button
                className="h-6 w-6 p-0"
                disabled={isMutating}
                onClick={handleMarkingAsToSchedule}
                size="sm"
                variant="ghost"
              >
                <X className="h-4 w-4" />
              </Button>
            }
            content="Cancel scheduled wafer request"
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}
